import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import SignInActivityItem from "@/components/sign-in-activity-item";
import { CardSkeleton } from "@/components/skeletons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { SignInRecord } from "@/lib/signins";
import { queryClient, trpc } from "@/utils/trpc";

export default function SignInDetail() {
  const { signInId } = useParams();
  const id = signInId || "";

  const signInQuery = useQuery(trpc.getSignIn.queryOptions({ signInId: id }));

  const updateFlagged = useMutation({
    ...trpc.updateSignInFlagged.mutationOptions(),
    onSuccess: () => {
      toast.success("Flag status updated");
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : null;
      toast.error(message || "Failed to update flag status");
    },
  });

  if (signInQuery.isLoading) {
    return <CardSkeleton lines={6} />;
  }

  if (signInQuery.error || !signInQuery.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Unable to load sign-in</AlertTitle>
        <AlertDescription>
          {signInQuery.error?.message ||
            "This sign-in could not be found. It may have been removed."}
        </AlertDescription>
      </Alert>
    );
  }

  const signIn = signInQuery.data;
  const record: SignInRecord = {
    id: signIn.id,
    userId: signIn.userId,
    workerName: signIn.workerName,
    jobTitle: signIn.jobTitle,
    siteId: signIn.siteId,
    siteName: signIn.siteName,
    signInTime: signIn.signInTime,
    signOutTime: signIn.signOutTime,
    method: signIn.method,
    flagged: signIn.flagged,
    distanceFromSite: signIn.distanceFromSite,
    withinGeofence: signIn.withinGeofence,
    timezone: signIn.timezone,
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Sign-in Details</CardTitle>
        <CardDescription>
          {signIn.siteName} • {format(new Date(signIn.signInTime), "PPpp")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <SignInActivityItem
          title={record.workerName}
          subtitle={record.jobTitle ?? undefined}
          signInTime={record.signInTime}
          signOutTime={record.signOutTime}
          method={record.method}
          flagged={record.flagged}
          distanceFromSite={record.distanceFromSite ?? undefined}
          withinGeofence={record.withinGeofence ?? undefined}
        />
        {record.flagged && (
          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              onClick={() =>
                updateFlagged.mutate({
                  signInId: record.id,
                  flagged: false,
                })
              }
              disabled={updateFlagged.isPending}
              className="w-full"
            >
              {updateFlagged.isPending ? "Updating…" : "Mark as reviewed"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
